import { useEffect, useState } from "react";
import PageShell from "@/components/PageShell";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Navigate, Link } from "react-router-dom";
import { BookOpen, Layers, Library, Save, Trash2, Plus, Loader2, LineChart, LifeBuoy } from "lucide-react";
import { toast } from "sonner";

const TABS = [
  { id: "courses", label: "Courses", icon: BookOpen },
  { id: "decks", label: "Flashcards", icon: Layers },
  { id: "dictionary", label: "Dictionary", icon: Library },
];

function Field({ label, value, onChange, textarea, testid }) {
  return (
    <div className="mb-4">
      <label className="block text-xs uppercase tracking-widest font-mono text-muted-foreground mb-2">{label}</label>
      {textarea ? (
        <textarea data-testid={testid} rows={textarea} value={value || ""} onChange={(e) => onChange(e.target.value)}
          className="w-full bg-muted border border-input px-3 py-2.5 rounded-sm outline-none focus:border-primary text-sm font-mono" />
      ) : (
        <input data-testid={testid} value={value || ""} onChange={(e) => onChange(e.target.value)}
          className="w-full bg-muted border border-input px-3 py-2.5 rounded-sm outline-none focus:border-primary text-sm" />
      )}
    </div>
  );
}

export default function Admin() {
  const { user } = useAuth();
  const [tab, setTab] = useState("courses");
  const [list, setList] = useState([]);
  const [selected, setSelected] = useState(null);
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);
  const [overview, setOverview] = useState(null);

  useEffect(() => {
    if (user && user.role === "admin") api.get("/admin/overview").then((r) => setOverview(r.data)).catch(() => {});
  }, [user]);

  useEffect(() => {
    setSelected(null);
    setDraft(null);
    const ep = tab === "courses" ? "/courses" : tab === "decks" ? "/flashcards" : "/dictionary";
    api.get(ep).then((r) => setList(r.data));
  }, [tab]);

  if (user === null) return <PageShell><div className="p-20 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-primary" /></div></PageShell>;
  if (!user || user.role !== "admin") return <Navigate to="/dashboard" replace />;

  const keyOf = (item) => (tab === "dictionary" ? item.term : item.slug);

  const openItem = async (item) => {
    if (tab === "dictionary") {
      setSelected(item.term);
      setDraft({ ...item });
      return;
    }
    const ep = tab === "courses" ? `/courses/${item.slug}` : `/flashcards/${item.slug}`;
    const { data } = await api.get(ep);
    setSelected(item.slug);
    setDraft(data);
  };

  const save = async () => {
    setSaving(true);
    try {
      let body = { ...draft };
      delete body._id; delete body.id;
      if (tab === "courses") {
        body.lessons = typeof body.lessons === "string" ? JSON.parse(body.lessons) : body.lessons;
        body.cases = typeof body.cases === "string" ? JSON.parse(body.cases) : (body.cases || []);
      }
      if (tab === "decks") body.cards = typeof body.cards === "string" ? JSON.parse(body.cards) : body.cards;

      const ep = tab === "courses" ? `/admin/courses/${selected}` : tab === "decks" ? `/admin/decks/${selected}` : `/admin/dictionary/${encodeURIComponent(selected)}`;
      await api.put(ep, body);
      toast.success("Saved");
      const listEp = tab === "courses" ? "/courses" : tab === "decks" ? "/flashcards" : "/dictionary";
      api.get(listEp).then((r) => setList(r.data));
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Save failed (check JSON syntax)");
    } finally {
      setSaving(false);
    }
  };

  const del = async () => {
    if (!window.confirm("Delete this item?")) return;
    const ep = tab === "courses" ? `/admin/courses/${selected}` : tab === "decks" ? `/admin/decks/${selected}` : `/admin/dictionary/${encodeURIComponent(selected)}`;
    await api.delete(ep);
    toast.success("Deleted");
    setSelected(null); setDraft(null);
    const listEp = tab === "courses" ? "/courses" : tab === "decks" ? "/flashcards" : "/dictionary";
    api.get(listEp).then((r) => setList(r.data));
  };

  const createNew = async () => {
    const slug = tab !== "dictionary" ? window.prompt("New slug (kebab-case):") : window.prompt("New term:");
    if (!slug) return;
    try {
      if (tab === "courses") await api.post("/admin/courses", { slug, title: slug, summary: "", category: "General", level: "Beginner", track: "foundations", lessons: [], cases: [] });
      else if (tab === "decks") await api.post("/admin/decks", { slug, title: slug, category: "General", description: "", cards: [] });
      else await api.post("/admin/dictionary", { term: slug, definition: "", category: "Legal", manual_note: "" });
      toast.success("Created");
      const listEp = tab === "courses" ? "/courses" : tab === "decks" ? "/flashcards" : "/dictionary";
      api.get(listEp).then((r) => setList(r.data));
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Create failed");
    }
  };

  const upd = (k, v) => setDraft({ ...draft, [k]: v });

  return (
    <PageShell>
      <div className="max-w-7xl mx-auto px-6 py-12">
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div>
            <span className="text-xs uppercase tracking-widest font-mono text-primary">Admin</span>
            <h1 className="font-serif text-5xl mt-2 mb-2">Content Editor</h1>
          </div>
          <div className="flex items-center gap-2">
            <Link to="/admin/support" data-testid="admin-support-link"
              className="flex items-center gap-2 border border-border px-4 py-2.5 rounded-sm text-sm text-muted-foreground hover:border-primary hover:text-primary transition-colors">
              <LifeBuoy className="w-4 h-4" /> Support Inbox
            </Link>
            <Link to="/admin/reconciliation" data-testid="admin-reconciliation-link"
              className="flex items-center gap-2 bg-primary text-primary-foreground px-4 py-2.5 rounded-sm text-sm hover:-translate-y-[1px] transition-transform">
              <LineChart className="w-4 h-4" /> Reconciliation
            </Link>
          </div>
        </div>
        {overview && (
          <p className="text-sm text-muted-foreground mb-8 font-mono">
            {overview.courses} courses · {overview.decks} decks · {overview.terms} terms · {overview.templates} templates · {overview.users} users
          </p>
        )}

        <div className="flex gap-2 mb-8">
          {TABS.map((t) => (
            <button key={t.id} onClick={() => setTab(t.id)} data-testid={`admin-tab-${t.id}`}
              className={`flex items-center gap-2 px-4 py-2.5 text-xs uppercase tracking-widest font-mono border transition-colors ${tab === t.id ? "bg-primary text-primary-foreground border-primary" : "bg-card border-border text-muted-foreground hover:border-primary"}`}>
              <t.icon className="w-4 h-4" /> {t.label}
            </button>
          ))}
        </div>

        <div className="grid lg:grid-cols-[300px_1fr] gap-px bg-border border border-border">
          {/* List */}
          <aside className="bg-card p-4 lg:max-h-[70vh] lg:overflow-y-auto">
            <button onClick={createNew} data-testid="admin-new"
              className="w-full flex items-center justify-center gap-2 border border-dashed border-border py-2.5 mb-3 text-sm text-muted-foreground hover:border-primary hover:text-primary transition-colors">
              <Plus className="w-4 h-4" /> New
            </button>
            {list.map((item, idx) => (
              <button key={`${keyOf(item)}-${idx}`} onClick={() => openItem(item)} data-testid={`admin-item-${keyOf(item)}`}
                className={`w-full text-left px-3 py-2.5 text-sm rounded-sm transition-colors ${selected === keyOf(item) ? "bg-muted text-foreground" : "text-muted-foreground hover:bg-muted/50"}`}>
                {item.title || item.term}
              </button>
            ))}
          </aside>

          {/* Editor */}
          <section className="bg-card p-8">
            {!draft ? (
              <div className="h-full flex items-center justify-center text-muted-foreground text-sm">Select an item to edit, or create a new one.</div>
            ) : (
              <>
                <div className="flex items-center justify-between mb-6">
                  <h2 className="font-serif text-2xl">Editing: <span className="text-primary">{selected}</span></h2>
                  <div className="flex gap-2">
                    <button onClick={del} data-testid="admin-delete" className="flex items-center gap-2 border border-border px-3 py-2 rounded-sm text-sm text-muted-foreground hover:border-destructive hover:text-destructive-foreground transition-colors">
                      <Trash2 className="w-4 h-4" /> Delete
                    </button>
                    <button onClick={save} disabled={saving} data-testid="admin-save" className="flex items-center gap-2 bg-primary text-primary-foreground px-4 py-2 rounded-sm text-sm hover:-translate-y-[1px] transition-transform disabled:opacity-60">
                      {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save
                    </button>
                  </div>
                </div>

                {tab === "courses" && (
                  <>
                    <Field label="Title" value={draft.title} onChange={(v) => upd("title", v)} testid="admin-field-title" />
                    <Field label="Subtitle" value={draft.subtitle} onChange={(v) => upd("subtitle", v)} />
                    <div className="grid grid-cols-3 gap-4">
                      <Field label="Category" value={draft.category} onChange={(v) => upd("category", v)} />
                      <Field label="Level" value={draft.level} onChange={(v) => upd("level", v)} />
                      <Field label="Track" value={draft.track} onChange={(v) => upd("track", v)} />
                    </div>
                    <Field label="Summary" value={draft.summary} onChange={(v) => upd("summary", v)} textarea={2} />
                    <Field label="Lessons (JSON)" value={typeof draft.lessons === "string" ? draft.lessons : JSON.stringify(draft.lessons, null, 2)} onChange={(v) => upd("lessons", v)} textarea={12} testid="admin-field-lessons" />
                    <Field label="Cited Cases (JSON)" value={typeof draft.cases === "string" ? draft.cases : JSON.stringify(draft.cases || [], null, 2)} onChange={(v) => upd("cases", v)} textarea={8} />
                  </>
                )}

                {tab === "decks" && (
                  <>
                    <Field label="Title" value={draft.title} onChange={(v) => upd("title", v)} testid="admin-field-title" />
                    <Field label="Category" value={draft.category} onChange={(v) => upd("category", v)} />
                    <Field label="Description" value={draft.description} onChange={(v) => upd("description", v)} textarea={2} />
                    <Field label="Cards (JSON)" value={typeof draft.cards === "string" ? draft.cards : JSON.stringify(draft.cards, null, 2)} onChange={(v) => upd("cards", v)} textarea={14} testid="admin-field-cards" />
                  </>
                )}

                {tab === "dictionary" && (
                  <>
                    <Field label="Term" value={draft.term} onChange={(v) => upd("term", v)} testid="admin-field-term" />
                    <Field label="Category" value={draft.category} onChange={(v) => upd("category", v)} />
                    <Field label="Definition" value={draft.definition} onChange={(v) => upd("definition", v)} textarea={4} testid="admin-field-definition" />
                    <Field label="Note (contested-theory context)" value={draft.manual_note} onChange={(v) => upd("manual_note", v)} textarea={2} />
                  </>
                )}
              </>
            )}
          </section>
        </div>
      </div>
    </PageShell>
  );
}
